'use client';

import React, { useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';

type Step = 1 | 2 | 3;

interface FormData {
  loanAmount: number;
  loanType: string;
  creditRange: string;
  firstName: string;
  phone: string;
}

const LOAN_TYPES = [
  { value: 'business', label: 'Business Loan', desc: 'Working capital, expansion, equipment' },
  { value: 'personal', label: 'Personal Loan', desc: 'Debt consolidation, major purchases' },
  { value: 'healthcare', label: 'Healthcare Loan', desc: 'Medical bills, procedures, wellness' },
  { value: 'sba', label: 'SBA Loan', desc: 'Government-backed small business funding' },
];

const CREDIT_OPTIONS = [
  { value: 'poor', label: 'Poor (below 580)', desc: 'Secured or alternative programs available' },
  { value: 'fair', label: 'Fair (580–669)', desc: 'Select personal & business programs open' },
  { value: 'good', label: 'Good (670–739)', desc: 'Most loan programs available' },
  { value: 'very_good', label: 'Very Good (740+)', desc: 'All programs + best rates' },
];

function formatAmount(val: number): string {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toLocaleString()}`;
}

function getAmountLabel(val: number): string {
  if (val <= 25000) return 'Personal or starter business loan range';
  if (val <= 100000) return 'Small business or consolidation range';
  if (val <= 500000) return 'Mid-size business loan range';
  return 'Large business or commercial loan range';
}

export default function QualifyForm() {
  const router = useRouter();
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<FormData>({
    loanAmount: 50000,
    loanType: '',
    creditRange: '',
    firstName: '',
    phone: '',
  });

  const sliderPct = Math.round(((data.loanAmount - 5000) / (2000000 - 5000)) * 100);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, loanAmount: Number(e.target.value) }));
  }, []);

  const canProceedStep2 = data.loanType !== '' && data.creditRange !== '';
  const canProceedStep3 = data.firstName.trim().length > 0 && data.phone.replace(/\D/g, '').length === 10;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams({
      amount: String(data.loanAmount),
      loanType: data.loanType,
      creditRange: data.creditRange,
      firstName: data.firstName,
    });
    router.push(`/pre-qualification?${params.toString()}`);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-pale border border-sky/20 mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-sky" />
          <span className="text-xs font-semibold text-sky-dark font-body">Soft credit check — no score impact</span>
        </div>
        <h2 className="font-heading font-bold text-2xl lg:text-3xl text-slate leading-tight mb-2">
          Find Your Loan Options
        </h2>
        <p className="font-body text-sm text-slate-fog">
          Takes 90 seconds · Programs for every borrower profile
        </p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-0 mb-8">
        {([1, 2, 3] as Step[]).map((s, idx) => {
          const labels = ['Loan Amount', 'Loan Type', 'Contact'];
          const isDone = step > s;
          const isActive = step === s;
          return (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold font-body transition-all duration-300 ${
                    isDone
                      ? 'bg-sage text-white shadow-sage/40 shadow-md'
                      : isActive
                      ? 'bg-sky text-white shadow-sky/40 shadow-md'
                      : 'bg-slate-mist text-slate-fog'
                  }`}
                >
                  {isDone ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : s}
                </div>
                <span
                  className={`text-xs font-medium font-body whitespace-nowrap ${
                    isActive ? 'text-sky' : isDone ? 'text-sage-dark' : 'text-slate-fog'
                  }`}
                >
                  {labels[idx]}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`step-connector mx-2 mb-4 ${isDone || (isActive && idx < 1) ? 'complete' : ''}`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* ── STEP 1: Loan Amount ── */}
        {step === 1 && (
          <div className="space-y-6 animate-enter">
            <div>
              <label className="block font-heading font-semibold text-slate text-sm mb-1">
                How much do you need?
              </label>
              <p className="text-xs text-slate-fog font-body mb-5">
                Select your desired loan amount
              </p>

              <div className="text-center mb-6">
                <span className="font-heading font-black text-5xl text-slate tracking-tight">
                  {formatAmount(data.loanAmount)}
                </span>
                <p className="text-xs text-sky font-body mt-2 font-medium">
                  {getAmountLabel(data.loanAmount)}
                </p>
              </div>

              <input
                type="range"
                min={5000}
                max={2000000}
                step={5000}
                value={data.loanAmount}
                onChange={handleAmountChange}
                className="income-slider w-full"
                style={{ '--slider-pct': `${sliderPct}%` } as React.CSSProperties}
                aria-label="Desired loan amount"
              />
              <div className="flex justify-between text-xs text-slate-fog font-body mt-2">
                <span>$5,000</span>
                <span>$2,000,000+</span>
              </div>
            </div>

            {/* Quick select amounts */}
            <div className="grid grid-cols-2 gap-2 pt-2">
              {[
                { label: '$10K–$25K', value: 15000, tag: 'Personal' },
                { label: '$25K–$100K', value: 50000, tag: 'Small Biz' },
                { label: '$100K–$500K', value: 250000, tag: 'Mid-Size' },
                { label: '$500K+', value: 750000, tag: 'Commercial' },
              ].map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => setData((prev) => ({ ...prev, loanAmount: b.value }))}
                  className="flex items-center justify-between px-3 py-2 rounded-xl border border-slate-mist hover:border-sky/40 hover:bg-sky-pale transition-all text-left group"
                >
                  <span className="text-xs font-medium text-slate font-body">{b.label}</span>
                  <span className="text-xs text-sky font-body opacity-0 group-hover:opacity-100 transition-opacity">{b.tag}</span>
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setStep(2)}
              className="w-full py-4 bg-sky hover:bg-sky-dark text-white font-heading font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sky btn-pulse text-base mt-2"
            >
              Continue to Loan Type
              <Icon name="ArrowRightIcon" size={18} />
            </button>

            <p className="text-center text-xs text-slate-fog font-body">
              No hard credit pull · Your info is never sold
            </p>
          </div>
        )}

        {/* ── STEP 2: Loan Type + Credit ── */}
        {step === 2 && (
          <div className="space-y-5 animate-enter">
            <div>
              <label className="block font-heading font-semibold text-slate text-sm mb-3">
                What type of loan are you looking for?
              </label>
              <div className="space-y-2">
                {LOAN_TYPES.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                      data.loanType === opt.value
                        ? 'border-sky bg-sky-pale' : 'border-slate-mist hover:border-sky/40 hover:bg-cloud'
                    }`}
                  >
                    <input
                      type="radio"
                      name="loanType"
                      value={opt.value}
                      checked={data.loanType === opt.value}
                      onChange={() => setData((prev) => ({ ...prev, loanType: opt.value }))}
                      className="mt-0.5 accent-sky"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate font-body">{opt.label}</p>
                      <p className="text-xs text-slate-fog font-body mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-heading font-semibold text-slate text-sm mb-3">
                Estimated Credit Score Range
              </label>
              <div className="space-y-2">
                {CREDIT_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                      data.creditRange === opt.value
                        ? 'border-sky bg-sky-pale' : 'border-slate-mist hover:border-sky/40 hover:bg-cloud'
                    }`}
                  >
                    <input
                      type="radio"
                      name="creditRange"
                      value={opt.value}
                      checked={data.creditRange === opt.value}
                      onChange={() => setData((prev) => ({ ...prev, creditRange: opt.value }))}
                      className="mt-0.5 accent-sky"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate font-body">{opt.label}</p>
                      <p className="text-xs text-slate-fog font-body mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-3.5 border border-slate-mist text-slate font-heading font-semibold rounded-2xl hover:bg-cloud transition-all text-sm"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => canProceedStep2 && setStep(3)}
                disabled={!canProceedStep2}
                className={`flex-2 flex-1 py-3.5 font-heading font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 text-sm ${
                  canProceedStep2
                    ? 'bg-sky hover:bg-sky-dark text-white shadow-sky'
                    : 'bg-slate-mist text-slate-fog cursor-not-allowed'
                }`}
              >
                Continue
                <Icon name="ArrowRightIcon" size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Contact ── */}
        {step === 3 && (
          <div className="space-y-4 animate-enter">
            <div>
              <label className="block font-heading font-semibold text-slate text-sm mb-1.5">
                First Name
              </label>
              <input
                type="text"
                placeholder="Your first name"
                value={data.firstName}
                onChange={(e) => setData((prev) => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-4 py-3.5 rounded-xl border border-slate-mist bg-cloud text-slate font-body text-sm focus:outline-none focus:border-sky focus:ring-2 focus:ring-sky/20 transition-all placeholder:text-slate-fog"
              />
            </div>
            <div>
              <label className="block font-heading font-semibold text-slate text-sm mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="(555) 000-0000"
                value={data.phone}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  let formatted = digits;
                  if (digits.length >= 6) formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                  else if (digits.length >= 3) formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                  setData((prev) => ({ ...prev, phone: formatted }));
                }}
                className="w-full px-4 py-3.5 rounded-xl border border-slate-mist bg-cloud text-slate font-body text-sm focus:outline-none focus:border-sky focus:ring-2 focus:ring-sky/20 transition-all placeholder:text-slate-fog"
              />
            </div>

            {/* Summary */}
            <div className="p-4 rounded-xl bg-cloud border border-slate-mist/60">
              <p className="text-xs font-semibold text-slate-fog font-body uppercase tracking-wide mb-2">Your Request Summary</p>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-fog font-body">Loan Amount</span>
                  <span className="font-semibold text-slate font-body">{formatAmount(data.loanAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-fog font-body">Loan Type</span>
                  <span className="font-semibold text-slate font-body capitalize">{data.loanType || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-fog font-body">Credit Range</span>
                  <span className="font-semibold text-slate font-body capitalize">{data.creditRange || '—'}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 py-3.5 border border-slate-mist text-slate font-heading font-semibold rounded-2xl hover:bg-cloud transition-all text-sm"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!canProceedStep3}
                className={`flex-1 py-3.5 font-heading font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 text-sm ${
                  canProceedStep3
                    ? 'bg-sky hover:bg-sky-dark text-white shadow-sky btn-pulse'
                    : 'bg-slate-mist text-slate-fog cursor-not-allowed'
                }`}
              >
                Find My Loan Options
                <Icon name="ArrowRightIcon" size={16} />
              </button>
            </div>

            <p className="text-center text-xs text-slate-fog font-body">
              By submitting, you agree to be contacted by a Lenders Home advisor.
            </p>
          </div>
        )}
      </form>
    </div>
  );
}